/**
 * Gravity-well continuity — the pure projection math.
 *
 * When diven at least one level, the board keeps the focal's PARENT rendered
 * behind the focal so the outer world doesn't vanish. This module computes the
 * parent-frame render state (which parent space, where it sits on screen, which
 * child is the "hole" the focal shows through) as a pure function of the current
 * viewport — no DOM, no React — so it's unit-testable and the render path stays
 * a thin projection.
 *
 * The parent-frame viewport is `popFocal(viewport, container)`, the exact
 * inverse of the dive rebase; by construction it lays the parent's container
 * slot exactly where the focal fills the screen, so the two layers align. See
 * `docs/superpowers/plans/2026-07-05-gravity-well-continuity.md`.
 */
import {
  asSource,
  panFor,
  popFocal,
  walkFocal,
  type BoardSpace,
  type SpaceLike,
  type Viewport,
} from "@archlang/scene/board";

export interface ContextLayerState {
  /** The focal's parent space — rendered behind the focal as the surround. */
  readonly parentSpace: BoardSpace;
  /** Id of the focal container within the parent — drawn frame-only so the
   *  focal layer shows through (the "hole"). */
  readonly holeId: string;
  /** Screen-pixel pan for the context transform (derived from `zoom` + size). */
  readonly pan: { readonly x: number; readonly y: number };
  /** Raw parent-frame zoom (CSS scale) — continuous, tracks the focal viewport. */
  readonly zoom: number;
}

/**
 * Compute the gravity-well context layer for the current viewport, or null when
 * there's nothing to render behind the focal (disabled, at root, or a malformed
 * focal path). Pure — the caller supplies the screen size and quantizes for LOD.
 */
export function computeContextLayer(
  root: SpaceLike,
  viewport: Viewport,
  screen: { width: number; height: number },
  enabled: boolean,
): ContextLayerState | null {
  if (!enabled) return null;
  // At root there is no parent to show.
  if (viewport.focalPath.length <= 1) return null;
  const steps = walkFocal(root, viewport.focalPath);
  // Require the walk to consume the ENTIRE focal path: `walkFocal` stops at the
  // first step it can't resolve, so `steps.length === focalPath.length` means
  // every level (and its container) exists. This also rejects a degenerate path
  // with a repeated id (e.g. [root, plat, plat] — a "dive into self" from a
  // stale/restored viewport): the walk can't find `plat` inside `plat`, stops
  // short, and the parent would otherwise resolve to the SAME space as the
  // focal, double-rendering it. An id-compare on the last step misses that case
  // because the duplicate makes the ids coincide; a length check does not.
  if (steps.length !== viewport.focalPath.length) return null;
  const container = steps[steps.length - 1]?.containerItem;
  if (!container) return null;
  const parentSpace = asSource(root).at(viewport.focalPath.slice(0, -1));
  const vpParent = popFocal(viewport, container);
  return {
    parentSpace,
    holeId: container.id,
    pan: panFor(vpParent, screen.width, screen.height),
    zoom: vpParent.zoom,
  };
}
